import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/cn';
import { normalizeLocaleTag, resolveIntlLocale } from '@/lib/i18nLocale';
import { SelectCombo } from './select';
import { nativePickerInputClassName, triggerNativePicker } from './input';

interface FilterGroupProps {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

export function FilterGroup({ children, className, style }: FilterGroupProps) {
    return (
        <div className={cn("flex items-center gap-1.5 flex-nowrap justify-start", className)} style={style}>
            {children}
        </div>
    );
}

interface FilterOption {
    value: string;
    label: string;
}

interface FilterSelectProps {
    value: string;
    onValueChange: (value: string) => void;
    options?: FilterOption[];
    placeholder?: string;
    className?: string;
    /** 호환용: 옵션에 "전체" 값이 있으면 `onValueChange`에서 빈 값을 그에 맞게 쓰면 됨. */
    emptyValue?: string;
}

export function FilterSelect({
    value,
    onValueChange,
    options = [],
    placeholder,
    className,
    emptyValue: _emptyValue,
}: FilterSelectProps) {
    return (
        <div className={cn("relative min-w-[140px]", className)}>
            <SelectCombo
                value={value}
                onValueChange={onValueChange}
                options={options}
                placeholder={placeholder}
                triggerClassName="h-[38px] bg-white"
            />
        </div>
    );
}

interface DateRangeFilterProps {
    startDate: string;
    endDate: string;
    onStartDateChange: (value: string) => void;
    onEndDateChange: (value: string) => void;
    label?: string;
}

interface DateFilterInputProps {
    value: string;
    onChange: (value: string) => void;
    ariaLabel: string;
    placeholder: string;
    locale: string;
    dir: 'ltr' | 'rtl';
    min?: string;
    max?: string;
}

function DateFilterInput({ value, onChange, ariaLabel, placeholder, locale, dir, min, max }: DateFilterInputProps) {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const openCalendar = () => {
        const input = inputRef.current;
        if (!input) return;
        input.focus({ preventScroll: true });
        triggerNativePicker(input);
    };

    return (
        <div className="relative w-[160px]">
            <input
                ref={inputRef}
                type="date"
                lang={locale}
                dir={dir}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={openCalendar}
                placeholder={placeholder}
                aria-label={ariaLabel}
                min={min}
                max={max}
                className={cn(
                    "h-10 w-full rounded-md border border-input bg-white px-3 py-2 pe-10 text-sm ring-offset-background transition-all",
                    "placeholder:text-muted-foreground hover:border-brand-500",
                    "focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:ring-offset-0 focus:border-brand-500",
                    nativePickerInputClassName
                )}
            />
            <button
                type="button"
                onMouseDown={(e) => {
                    e.preventDefault();
                    openCalendar();
                }}
                className="absolute inset-y-0 end-0 z-10 flex w-10 items-center justify-center rounded-e-md text-muted-foreground transition-colors hover:text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                aria-label={ariaLabel}
                lang={locale}
            >
                <Calendar size={16} aria-hidden="true" />
            </button>
        </div>
    );
}

export function DateRangeFilter({ startDate, endDate, onStartDateChange, onEndDateChange }: DateRangeFilterProps) {
    const { t, i18n } = useTranslation('common');
    const locale = resolveIntlLocale(i18n.language);
    const direction = normalizeLocaleTag(i18n.language) === 'ar' ? 'rtl' : 'ltr';
    const datePlaceholder = 'YYMMDD';
    return (
        <div className="flex items-center gap-2">
            <DateFilterInput
                value={startDate}
                onChange={onStartDateChange}
                placeholder={datePlaceholder}
                ariaLabel={t('date.startDate')}
                locale={locale}
                dir={direction}
                max={endDate || undefined}
            />
            <span className="text-muted-foreground px-0.5">~</span>
            <DateFilterInput
                value={endDate}
                onChange={onEndDateChange}
                placeholder={datePlaceholder}
                ariaLabel={t('date.endDate')}
                locale={locale}
                dir={direction}
                min={startDate || undefined}
            />
        </div>
    );
}

interface ManualTimePickerProps {
    ampm: string;
    hour: string | number;
    minute: string | number;
    onChange: (field: string, value: string) => void;
}

export function ManualTimePicker({ ampm, hour, minute, onChange }: ManualTimePickerProps) {
    return (
        <div className="flex items-center gap-2">
            <SelectCombo
                value={ampm}
                onValueChange={(val) => onChange('ampm', val)}
                options={[
                    { value: 'AM', label: 'AM' },
                    { value: 'PM', label: 'PM' }
                ]}
                className="w-20"
                triggerClassName="h-10 bg-white"
            />
            <div className="flex items-center gap-1 bg-white border border-input rounded-md px-2 h-10 transition-all hover:border-brand-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:ring-offset-0 focus-within:border-brand-500">
                <input
                    type="number"
                    min="1"
                    max="12"
                    value={hour}
                    onChange={(e) => onChange('hour', e.target.value)}
                    className="w-10 text-center text-base border-none outline-none appearance-none bg-transparent placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="12"
                />
                <span className="text-muted-foreground font-medium">:</span>
                <input
                    type="number"
                    min="0"
                    max="59"
                    value={minute}
                    onChange={(e) => onChange('minute', e.target.value)}
                    className="w-10 text-center text-base border-none outline-none appearance-none bg-transparent placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="00"
                />
            </div>
        </div>
    );
}
